import React, { useEffect } from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import ImpactCard from '../../components/public/ImpactCard';
import PartnerOptionCard from '../../components/public/PartnerOptionCard';
import PricingTierCard from '../../components/public/PricingTierCard';
import SponsorForm from '../../components/public/SponsorForm';
import FAQAccordion from '../../components/public/FAQAccordion';
import { trackEvent } from '../../publicSite/analytics';
import { APPLY_ROUTE, UPDATES_ROUTE, withBase } from '../../publicSite/config';

export default function PartnershipsSponsorsPage() {
  useEffect(() => {
    trackEvent('public_partnerships_view');
  }, []);

  return (
    <PublicLayout>
      <section className="about-hero">
        <div className="about-hero-inner">
          <div>
            <div className="ps-hero-eyebrow" style={{ color: 'var(--shem-gold-400)' }}>Partnerships</div>
            <h1>Partner with School of Shem</h1>
            <div className="about-hero-accent" />
            <p>Sponsor a child. Empower a youth. Strengthen a community.</p>
            <div className="ps-hero-actions" style={{ marginTop: '24px' }}>
              <button className="btn ps-btn-primary" type="button">Sponsor Monthly</button>
              <button className="btn ps-btn-outline" type="button">Sponsor Once</button>
            </div>
            <div className="ps-chip-row" style={{ marginTop: '18px' }}>
              <span className="ps-chip">Secure Giving</span>
              <span className="ps-chip">Receipts Provided</span>
              <span className="ps-chip">Transparent Impact</span>
            </div>
          </div>
          <div className="about-hero-panel">
            <div style={{ padding: '28px' }}>
              <h3 style={{ color: 'var(--shem-gold-400)', marginBottom: '12px' }}>Partnerships & Sponsors</h3>
              <p>
                Sponsor a child, empower a youth group, and fund resources that build lasting Kingdom impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="ps-section" style={{ background: 'var(--shem-surface-2)' }}>
        <div className="ps-section-inner">
          <h2 className="ps-section-title">Ways to partner</h2>
          <div className="ps-card-grid">
            <PartnerOptionCard
              title="Sponsor a Student (Monthly)"
              description="Provide consistent support for a learner’s growth and mentorship."
              amounts={['R150', 'R300', 'R500', 'R1000']}
            />
            <PartnerOptionCard
              title="Sponsor a Youth Group"
              description="Fund group sessions and leadership development for youth communities."
              amounts={['R500', 'R1000', 'R2000', 'R5000']}
            />
            <PartnerOptionCard
              title="Fund Learning Resources"
              description="Equip classrooms with learning tools, books, and digital resources."
              amounts={['R300', 'R750', 'R1500', 'R3000']}
            />
            <PartnerOptionCard
              title="Corporate/Church Partnership"
              description="Structured sponsorship packages with invoices and receipts."
              amounts={['R5000', 'R10000', 'R25000', 'R50000']}
            />
          </div>
        </div>
      </section>

      <section className="ps-section">
        <div className="ps-section-inner">
          <h2 className="ps-section-title">Choose a sponsorship package</h2>
          <div className="ps-card-grid">
            <PricingTierCard
              title="Foundation Partner"
              amount="R300 / month"
              bullets={["Supports one learner", "Weekly accountability", "Monthly updates"]}
              impact="Supports 1 youth/month"
            />
            <PricingTierCard
              title="Builder Partner"
              amount="R750 / month"
              bullets={["Supports multiple learners", "Resources + mentorship", "Impact reporting"]}
              impact="Supports 3 youth/month"
            />
            <PricingTierCard
              title="Legacy Partner"
              amount="R1500 / month"
              bullets={["Sponsor a group", "Leadership development", "Quarterly briefing"]}
              impact="Supports 5+ youth/month"
            />
          </div>
          <div className="ps-hero-actions" style={{ marginTop: '16px' }}>
            <button className="btn ps-btn-outline" type="button">Custom Amount</button>
          </div>
        </div>
      </section>

      <section className="ps-section about-band">
        <div className="ps-section-inner">
          <h2>Impact you create</h2>
          <div className="ps-card-grid">
            <ImpactCard title="Education access" description="Open doors for learners to receive structured training." />
            <ImpactCard title="Mentorship & community" description="Sustain accountability, support, and spiritual formation." />
            <ImpactCard title="Youth group development" description="Build environments where youth lead with purpose." />
          </div>
          <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.8)' }}>
            Impact promise: Every partnership supports real programs with transparent reporting and measurable outcomes.
          </p>
        </div>
      </section>

      <section className="ps-section">
        <div className="ps-section-inner">
          <h2 className="ps-section-title">Become a sponsor</h2>
          <SponsorForm />
        </div>
      </section>

      <section className="ps-section" style={{ background: 'var(--shem-surface-2)' }}>
        <div className="ps-section-inner">
          <h2 className="ps-section-title">Stories of impact</h2>
          <div className="ps-card-grid">
            {["Sophie N.", "David M.", "Grace L."].map((name) => (
              <div key={name} className="ps-card">
                <h3>{name}</h3>
                <p>“School of Shem gave me a structure to build and the accountability to keep going.”</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ps-section">
        <div className="ps-section-inner">
          <h2 className="ps-section-title">Frequently asked questions</h2>
          <FAQAccordion
            items={[
              { key: 'where', title: 'Where does my sponsorship go?', body: 'Funds are allocated to student formation, resources, and program delivery.' },
              { key: 'receipt', title: 'Do I get a receipt?', body: 'Yes. Receipts are provided for all sponsorships.' },
              { key: 'anonymous', title: 'Can I sponsor anonymously?', body: 'Yes. Indicate this in the notes section.' },
              { key: 'business', title: 'Can my business sponsor a youth group?', body: 'Absolutely. We provide sponsorship packages with invoicing.' },
              { key: 'cancel', title: 'Can I cancel a monthly sponsorship?', body: 'Yes. Contact support for assistance.' },
              { key: 'updates', title: 'How do updates work?', body: 'We provide periodic updates on impact and progress.' },
              { key: 'secure', title: 'Is my payment secure?', body: 'We use secure processes and provide transparent records.' },
              { key: 'contact', title: 'Who can I contact?', body: 'Email info@schoolofshem.com for assistance.' }
            ]}
          />
        </div>
      </section>

      <section className="ps-section about-cta">
        <div className="ps-section-inner">
          <h2>Help build a generation of leaders.</h2>
          <div className="ps-hero-actions" style={{ marginTop: '16px' }}>
            <a className="btn ps-btn-primary" href={withBase(APPLY_ROUTE)}>Sponsor Monthly</a>
            <a className="btn ps-btn-outline" href={withBase(UPDATES_ROUTE)}>Talk to us</a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
